/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, RouterStateSnapshot } from '@angular/router';
import {
  AbstractFormComponent, BuilderNavigationService, CommonActions, FormActionPipelines, FormFlowState, FormulaBuilderComponent, FormulaPropagationService, highlightAll, whenFormInvalid, } from '@gh/builder-shared';
import { isFormulaEmpty, isFormulaValid } from '@gh/builder-shared-data';
import {
  ActionFlow, AppRouter, CanLeaveRoute, compoundLogicalAction, eitherAction, exitAction, getRouteInitData, LogicalAction, streamOptionalValueAction, } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode, getControlValue, setControlValue, UploadContext } from '@gh/core-ui';
import { wrapInObservable } from '@gh/core-util';
import { from$ } from '@gh/rx';
import { map$, filter$ } from '@gh/rx/operators';
import { assign, find, get } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  ItemDetails,
  needsFormulaPropagation,
  UomSharedListItem,
} from '../../resources/component-and-item-details.model';
import { ItemDetailsBusinessService } from '../../resources/item-details-business.service';
import { ComponentOrItemFormState } from '../component-or-item-form-state';
import { ItemDetailsFormState } from './item-details-form.service';
import { InputMatchingService } from '../../../shared/services/input-matching.service';

enum ItemDetailsTab { Formula = 'FORMULA', Picture = 'PICTURE', Memo = 'MEMO' }

@Component({
  standalone: false,
  selector: 'gh-item-details-tab-container',
  templateUrl: 'item-details-tab-container.component.html',
  styleUrls: ['../../../details-tab-container.scss'],
  providers: [
    ItemDetailsFormState,
    { provide: ComponentOrItemFormState, useExisting: ItemDetailsFormState },
    UploadContext,
  ],
})
export class ItemDetailsTabContainerComponent extends AbstractFormComponent<ItemDetails>
  implements OnInit, AfterViewInit, CanLeaveRoute, OnDestroy {

  uoms: UomSharedListItem[];
  selectedTab = ItemDetailsTab.Formula;
  matchingInput: string[] = [];
  private inputSubscription: Subscription;

  private ignoreInputValueChanges = false;

  @ViewChild(FormulaBuilderComponent, { static: false }) formulaBuilder: FormulaBuilderComponent;
  @ViewChild('inputFieldWrapper', { static: true }) inputFieldWrapper: ElementRef;
  @ViewChild('codeField') fieldToFocus!: ElementRef;

  constructor(private route: ActivatedRoute,
              public form: ItemDetailsFormState,
              private navigationService: BuilderNavigationService,
              private businessService: ItemDetailsBusinessService,
              private formulaPropagation: FormulaPropagationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private router: AppRouter,
              private inputMatchingService: InputMatchingService,
              private cdr: ChangeDetectorRef) {
    super();
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_item_create',
      [FormMode.EditMode]: 'lbl_item_edit',
      [FormMode.ViewMode]: 'lbl_item_view',
    }[this.form.mode];
  }

  get isFormulaNotPropagatable(): boolean {
    const formula = getControlValue(this.form.group, 'formula');
    return isFormulaEmpty(formula) || !isFormulaValid(formula);
  }

  get completeFlow(): ActionFlow<FormFlowState<ItemDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, (state) => this.handleErrors(state)),
      execute: this.saveAndPropagateAction,
      finish: (state: FormFlowState<ItemDetails>) => {
        const { value, form } = state;
        this.ignoreInputValueChanges = true;
        if (form.createMode) {
          this.router.switchTo(['/setup/estimating/items', value.id]);
        } else {
          form.build(value);
          setTimeout(() => this.ignoreInputValueChanges = false, 1000);
        }
      },
    });
  }

  get saveAndPropagateAction(): LogicalAction<FormFlowState<ItemDetails>> {
    const saveAction = this.commonActions.execute((state: FormFlowState<ItemDetails>) =>
      this.businessService.save(state.value));
    return compoundLogicalAction('execute.success', 'execute.fail', {
      'start': eitherAction(({ value }: FormFlowState<ItemDetails>) =>
        value.id ? (needsFormulaPropagation(value)) : false).bindAs('save-and-propagate', 'save'),
      'save': saveAction.bindAs('success', 'fail'),
      'save-and-propagate': saveAction.bindAs('propagate-formula', 'fail'),
      'propagate-formula': streamOptionalValueAction((state) =>
        this.propagateFormula().pipe(map$(() => state))).bindAs('success', 'fail'),
      'success': exitAction('execute.success'),
      '*': exitAction('execute.fail'),
    });
  }

  ngOnInit(): void {
    const { item, ...init } = getRouteInitData(this.route);

    this.form.build(item);
    this.setupInputWatcher();

    assign(this, init);
  }
  ngAfterViewInit(): void {
    if (this.form.mode !== FormMode.CreateMode) {
      return;
    }
    setTimeout(() => this.fieldToFocus.nativeElement.focus());
  }

  private setupInputWatcher(): void {
    const codeControl = this.form.group.get('code');
    if (codeControl) {
      this.inputSubscription = this.inputMatchingService.setupInputWatcher(
        codeControl.valueChanges,
        () => this.ignoreInputValueChanges,
        searchString => this.inputMatchingService.loadMatchingInput(
          searchString,
          this.businessService.getMatchingItemCodes.bind(this.businessService),
          codes => (this.matchingInput = codes as string[]),
          this.cdr
        )
      );
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.inputMatchingService.clearMatchingInputOnClick(
      event,
      this.inputFieldWrapper.nativeElement,
      () => (this.matchingInput = []),
      this.cdr
    );
  }

  trackInput(index: number, code: string): string {
    return code;
  }

  ngOnDestroy(): void {
    this.inputMatchingService.unsubscribe(this.inputSubscription);
  }

  canDeactivate(nextState?: RouterStateSnapshot): Observable<boolean> | boolean {
    // Avoid standard deactivation flow when we need to show propagate formula dialog
    // We have to do this, because formula dialog is defined in another package
    // and loaded dynamically by @angular/router.
    // When we are in leaving current route and start another route transition (and wait when it completes)
    // we block navigation at all!!!
    // This way we reject current deactivation, start leave flow manually and navigate when it completes
    if (this.form.pristine) {
      return true;
    } else if (!needsFormulaPropagation(this.form.submitValue)) {
      return super.canDeactivate();
    }

    wrapInObservable(super.canDeactivate()).pipe(
      filter$(Boolean))
      .subscribe(() => {
        this.form.markAsPristine();
        this.router.navigateByUrl(nextState!.url);
      });

    return false;
  }

  getUomName(uomId?: ID): string {
    return uomId ? <string>get(find(this.uoms, { id: uomId }), 'name', '') : '';
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.form.id,
      entityType: 'ITEM',
      label: this.title,
    });
  }

  onFormulaChange(): void {
    this.setFormulaNeedPropagation(true);
  }

  onFormulaPropagate(): void {
    if (this.validateFormula()) {
      this.propagateFormula();
    }
  }

  private handleErrors(state: FormFlowState<ItemDetails>): void {
    highlightAll(state);

    this.validateFormula();
  }

  private validateFormula(): boolean {
    if (this.form.isFormulaInvalid()) {
      this.selectedTab = ItemDetailsTab.Formula;
      this.formulaBuilder.test();
      return false;
    }
    return true;
  }

  private propagateFormula(): Observable<void> {
    const { formulaContext } = getRouteInitData(this.route);
    return from$(this.formulaPropagation.propagateFormula({
      formulaContext,
      elementId: getControlValue(this.form.group, 'id'),
      elementCode: getControlValue(this.form.group, 'code'),
      isComponent: false,
      newFormula: getControlValue(this.form.group, 'formula'),
      oldFormula: getControlValue(this.form.group, 'oldFormula'),
    }).then(() => this.setFormulaNeedPropagation(false)));
  }

  private setFormulaNeedPropagation(needPropagation: boolean): void {
    setControlValue(this.form.group, 'isFormulaNeedPropagation', needPropagation);
  }
}
